/**
 * Presents `useValidateDefinition`'s issues to an admin: classify (blocker vs fixable gap), name
 * (`definition.screens.2.…options` → "Screen 3 · Question 1" / "Answer options"), group by owning
 * node — Zod reports per field, an author thinks per question. Stateless: reactive in, computed out.
 */
import { computed, toValue } from 'vue'

import type { MaybeRefOrGetter } from 'vue'
import type {
  AssessmentValidationBlocker,
  AssessmentValidationGroup,
  AssessmentValidationGroupIssue,
  AssessmentValidationIssue,
  AssessmentValidationTarget,
  UseValidationReportReturn,
} from './interfaces'

import { withCount } from '@shared/lib/format'

/** Problems no amount of typing fixes: an unauthorable `schemaVersion` (refinement 8) and a stored
 *  observation rubric (B10). `levels` matches at that exact depth only — an issue INSIDE a band
 *  (`rubrics.<id>.levels.<i>.<field>`) is an ordinary fixable gap. */
const isBlockerPath = (segments: (string | number)[]): boolean => {
  const [head, , field] = segments
  if (segments.at(-1) === 'schemaVersion') {
    return true
  }
  return head === 'rubrics' && segments.length === 3 && (field === 'kind' || field === 'levels')
}

/** Fallback label, so an unmapped field degrades to English rather than to a JSON key. */
const humanize = (segment: string): string => {
  const spaced = segment.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase()
}

/** Keyed as the document spells it, so a schema rename surfaces as a missing label, not a wrong one. */
const FIELD_LABELS: Record<string, string> = {
  title: 'Title',
  description: 'Description',
  disciplineCode: 'Discipline',
  grade: 'Grade',
  name: 'Name',
  prompt: 'Question text',
  options: 'Answer options',
  formats: 'Answer formats',
  correctIndex: 'Correct answer',
  answers: 'Correct answers',
  answer: 'Correct answer',
  instructions: 'Tutor instructions',
  standards: 'Standards',
  cta: 'Button text',
  layout: 'Layout',
  isLast: 'Last screen flag',
  text: 'Text',
  items: 'List items',
  url: 'File',
  alt: 'Image description',
  score: 'Score',
  type: 'Type',
  id: 'Id',
}

const fieldLabel = (segment: string | number): string =>
  typeof segment === 'number'
    ? `#${segment + 1}`
    : FIELD_LABELS[segment] ?? humanize(segment)

/** Collection + index pairs that name a node the editor can point at. */
const OWNER_LABELS: Record<string, string> = {
  screens: 'Screen',
  activities: 'Question',
  content: 'Content block',
  levels: 'Band',
}

/** Schema wrappers an author never sees — "Answer options", not "Interaction → Answer options". */
const TRANSPARENT_SEGMENTS = new Set(['interaction'])

interface Split {
  /** The owning node's path — the group key. */
  key: string
  /** Breadcrumb of owner labels. */
  label: string
  field: string
}

/** Splits a path into "which node" and "which field of it". `definition` is dropped — every
 *  definition-level path carries it. A `rubrics.<id>` head is the caller's job: it alone can look
 *  the name up. */
const splitPath = (segments: (string | number)[]): Split => {
  const owners: string[] = []
  const keyParts: (string | number)[] = []
  const fields: string[] = []
  let ownerEnd = 0

  const body = segments[0] === 'definition' ? segments.slice(1) : segments
  const prefix = segments[0] === 'definition' ? ['definition'] : []

  for (let index = 0; index < body.length; index += 1) {
    const segment = body[index]
    const next = body[index + 1]
    if (typeof segment !== 'string') {
      break
    }
    const ownerLabel = OWNER_LABELS[segment]

    if (ownerLabel !== undefined && typeof next === 'number') {
      owners.push(`${ownerLabel} ${next + 1}`)
      keyParts.push(segment, next)
      ownerEnd = index + 2
      index += 1
      continue
    }
    break
  }

  body.slice(ownerEnd).forEach((segment) => {
    if (typeof segment === 'string' && TRANSPARENT_SEGMENTS.has(segment)) {
      return
    }
    // "Answer options #2" beats "Answer options → Options → #2".
    if (typeof segment === 'string' && OWNER_LABELS[segment] && fields.length > 0) {
      return
    }
    fields.push(fieldLabel(segment))
  })

  return {
    key: [...prefix, ...keyParts].join('.'),
    label: owners.join(' · '),
    // Empty when the path ends on the owner itself; the message says what is missing.
    field: fields.join(' → '),
  }
}

/** The field of a path already inside a rubric: "Band 1 → Label", "Tutor instructions". */
const rubricField = (segments: (string | number)[]): string => {
  const split = splitPath(segments)
  return [split.label, split.field].filter(part => part !== '').join(' → ')
}

/** The catalog as the RAW document holds it — unparsed, so nothing is assumed. */
const rubricsOf = (document: unknown): Record<string, unknown> => {
  const rubrics = (document as { rubrics?: unknown } | null)?.rubrics
  return rubrics !== null && typeof rubrics === 'object' && !Array.isArray(rubrics)
    ? rubrics as Record<string, unknown>
    : {}
}

/** An unparseable rubric still has a readable `name`; the id is what no admin has ever seen. */
const rubricLabel = (document: unknown, id: string): string => {
  const rubric = rubricsOf(document)[id]
  const name = (rubric as { name?: unknown } | undefined)?.name
  return typeof name === 'string' && name.trim() !== ''
    ? `Rubric "${name.trim()}"`
    : `Rubric "${id}"`
}

/** Rubrics are authored inside the question that references them, so that question is where a
 *  rubric lives on screen. */
const findRubricOwner = (document: unknown, rubricId: string): AssessmentValidationTarget | null => {
  const screens = (document as { definition?: { screens?: unknown } } | null)?.definition?.screens
  if (!Array.isArray(screens)) {
    return null
  }
  for (const screen of screens) {
    const activities = (screen as { activities?: unknown })?.activities
    if (!Array.isArray(activities)) {
      continue
    }
    for (const activity of activities) {
      const rubrics = (activity as { rubrics?: unknown })?.rubrics
      if (Array.isArray(rubrics) && rubrics.includes(rubricId)) {
        return {
          screenId: (screen as { id?: unknown }).id as string ?? null,
          activityId: (activity as { id?: unknown }).id as string ?? null,
        }
      }
    }
  }
  return null
}

/**
 * @param document the RAW working document — the only place a rubric's `name` and the
 *   screen/activity ids can be read from, since a document with issues never parsed.
 */
export const useValidationReport = (
  issues: MaybeRefOrGetter<AssessmentValidationIssue[]>,
  document: MaybeRefOrGetter<unknown>,
): UseValidationReportReturn => {
  const refIssues = computed<AssessmentValidationIssue[]>(() => toValue(issues))

  const refBlockers = computed<AssessmentValidationBlocker[]>(() => {
    const doc = toValue(document)
    const byLabel = new Map<string, AssessmentValidationBlocker>()

    refIssues.value.filter(issue => isBlockerPath(issue.segments)).forEach((issue) => {
      const [head, id] = issue.segments
      const label = head === 'rubrics' && typeof id === 'string'
        ? rubricLabel(doc, id)
        : 'This template'
      // First message wins: `kind` and `levels` are two spellings of one fact.
      if (!byLabel.has(label)) {
        byLabel.set(label, { label, message: issue.message })
      }
    })

    return [...byLabel.values()]
  })

  const refGroups = computed<AssessmentValidationGroup[]>(() => {
    const doc = toValue(document)
    const byKey = new Map<string, AssessmentValidationGroup>()

    refIssues.value.filter(issue => !isBlockerPath(issue.segments)).forEach((issue) => {
      const [head, id] = issue.segments
      const split = splitPath(issue.segments)
      const isRubric = head === 'rubrics' && typeof id === 'string'
      const key = isRubric ? `rubrics.${id}` : (split.key || 'definition')
      const label = isRubric
        ? rubricLabel(doc, id)
        : split.label || 'Assessment'
      // The rubric map's id segment is not an index, so the field is derived from the segments
      // after it — and its inner owner ("Band 1") belongs to the field, not the heading.
      const field = isRubric ? rubricField(issue.segments.slice(2)) : split.field

      const group = byKey.get(key) ?? { key, label, issues: [] }
      const entry: AssessmentValidationGroupIssue = { ...issue, field }
      // Same field, same message twice is Zod being thorough, not two problems.
      if (!group.issues.some(existing => existing.field === entry.field && existing.message === entry.message)) {
        group.issues.push(entry)
      }
      byKey.set(key, group)
    })

    return [...byKey.values()]
  })

  const refHasProblems = computed<boolean>(() =>
    refBlockers.value.length > 0 || refGroups.value.length > 0,
  )

  /** Counts the places to FIX — not the Zod issues behind them, and not the blockers, whose own
   *  rows already say it. So `null` is not "all clear": see the page hook. */
  const refSummary = computed<string | null>(() => {
    const groups = refGroups.value.length

    return groups === 0
      ? null
      : `${withCount(groups, { one: 'place', other: 'places' })} to fix`
  })

  const resolveTarget = (issue: AssessmentValidationIssue): AssessmentValidationTarget | null => {
    const doc = toValue(document)
    const [head, id] = issue.segments

    if (head === 'rubrics' && typeof id === 'string') {
      return findRubricOwner(doc, id)
    }

    const body = head === 'definition' ? issue.segments.slice(1) : issue.segments
    if (body[0] !== 'screens' || typeof body[1] !== 'number') {
      return null
    }
    const screens = (doc as { definition?: { screens?: unknown } } | null)?.definition?.screens
    const screen = Array.isArray(screens) ? screens[body[1]] : undefined
    if (screen === undefined) {
      return null
    }
    const screenId = (screen as { id?: unknown }).id as string ?? null

    if (body[2] !== 'activities' || typeof body[3] !== 'number') {
      return { screenId, activityId: null }
    }
    const activities = (screen as { activities?: unknown }).activities
    const activity = Array.isArray(activities) ? activities[body[3]] : undefined

    return {
      screenId,
      activityId: activity === undefined ? null : (activity as { id?: unknown }).id as string ?? null,
    }
  }

  return {
    refBlockers,
    refGroups,
    refSummary,
    refHasProblems,
    resolveTarget,
  }
}
